// src/components/black-friday/metric-cards.tsx
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowUp, ArrowDown, Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface MetricCardData {
  label: string;
  value2025: number;
  value2024: number;
  format: "number" | "currency" | "percentage" | "decimal";
  decimals?: number;
  tooltip?: string;
}

interface Props {
  metrics: {
    year2025: {
      impr: number;
      clicks: number;
      cost: number;
      conv: number;
      convValue: number;
    };
    year2024: {
      impr: number;
      clicks: number;
      cost: number;
      conv: number;
      convValue: number;
    };
  };
  comparisonLabel: string;
}

function formatValue(value: number, format: string, decimals: number = 0): string {
  if (isNaN(value) || !isFinite(value)) return "-";

  switch (format) {
    case "currency":
      return `$${value.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}`;
    case "percentage":
      return `${value.toFixed(decimals)}%`;
    case "decimal":
      return value.toFixed(decimals);
    case "number":
    default:
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
  }
}

function calculateChange(current: number, previous: number): number {
  if (previous === 0) return 0;
  return ((current - previous) / previous) * 100;
}

function MetricCard({ metric, comparisonLabel }: { metric: MetricCardData; comparisonLabel: string }) {
  const changePercent = calculateChange(metric.value2025, metric.value2024);
  const isPositive = changePercent > 0;
  const isNegative = changePercent < 0;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="space-y-2">
          {/* Top row: Label (left) and Value (right) */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-1">
              <p className="text-sm font-medium text-muted-foreground">{metric.label}</p>
              {metric.tooltip && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs text-sm">{metric.tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <p className="text-2xl font-bold">
              {formatValue(metric.value2025, metric.format, metric.decimals)}
            </p>
          </div>

          {/* Bottom row: Comparison */}
          <div className="flex items-center gap-1.5 text-xs">
            <span className="text-muted-foreground">vs:</span>
            <span className="text-muted-foreground">
              {formatValue(metric.value2024, metric.format, metric.decimals)}
            </span>
            <span
              className={`font-medium flex items-center gap-0.5 ${
                isPositive
                  ? "text-green-600 dark:text-green-400"
                  : isNegative
                    ? "text-red-600 dark:text-red-400"
                    : "text-gray-600"
              }`}
            >
              {isPositive && <ArrowUp className="w-3 h-3" />}
              {isNegative && <ArrowDown className="w-3 h-3" />}
              {changePercent > 0 ? "+" : ""}{changePercent.toFixed(1)}%
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function MetricCards({ metrics, comparisonLabel }: Props) {
  const { year2025, year2024 } = metrics;

  // Calculate derived metrics
  const ctr2025 = year2025.impr > 0 ? (year2025.clicks / year2025.impr) * 100 : 0;
  const ctr2024 = year2024.impr > 0 ? (year2024.clicks / year2024.impr) * 100 : 0;

  const cpc2025 = year2025.clicks > 0 ? year2025.cost / year2025.clicks : 0;
  const cpc2024 = year2024.clicks > 0 ? year2024.cost / year2024.clicks : 0;

  const cvr2025 = year2025.clicks > 0 ? (year2025.conv / year2025.clicks) * 100 : 0;
  const cvr2024 = year2024.clicks > 0 ? (year2024.conv / year2024.clicks) * 100 : 0;

  const cpa2025 = year2025.conv > 0 ? year2025.cost / year2025.conv : 0;
  const cpa2024 = year2024.conv > 0 ? year2024.cost / year2024.conv : 0;

  const roas2025 = year2025.cost > 0 ? year2025.convValue / year2025.cost : 0;
  const roas2024 = year2024.cost > 0 ? year2024.convValue / year2024.cost : 0;

  const aov2025 = year2025.conv > 0 ? year2025.convValue / year2025.conv : 0;
  const aov2024 = year2024.conv > 0 ? year2024.convValue / year2024.conv : 0;

  const cpi2025 = year2025.impr > 0 ? (year2025.conv / year2025.impr) * 1000 : 0;
  const cpi2024 = year2024.impr > 0 ? (year2024.conv / year2024.impr) * 1000 : 0;

  const metricCards: MetricCardData[] = [
    {
      label: "Impressions",
      value2025: year2025.impr,
      value2024: year2024.impr,
      format: "number",
    },
    {
      label: "Clicks",
      value2025: year2025.clicks,
      value2024: year2024.clicks,
      format: "number",
    },
    {
      label: "CTR",
      value2025: ctr2025,
      value2024: ctr2024,
      format: "percentage",
      decimals: 1,
    },
    {
      label: "Cost",
      value2025: year2025.cost,
      value2024: year2024.cost,
      format: "currency",
      decimals: 0,
    },
    {
      label: "CPC",
      value2025: cpc2025,
      value2024: cpc2024,
      format: "currency",
      decimals: 2,
    },
    {
      label: "Conversions",
      value2025: year2025.conv,
      value2024: year2024.conv,
      format: "number",
    },
    {
      label: "Conv Value",
      value2025: year2025.convValue,
      value2024: year2024.convValue,
      format: "currency",
      decimals: 0,
    },
    {
      label: "CvR",
      value2025: cvr2025,
      value2024: cvr2024,
      format: "percentage",
      decimals: 1,
    },
    {
      label: "CPA",
      value2025: cpa2025,
      value2024: cpa2024,
      format: "currency",
      decimals: 2,
    },
    {
      label: "ROAS",
      value2025: roas2025,
      value2024: roas2024,
      format: "decimal",
      decimals: 1,
    },
    {
      label: "AOV",
      value2025: aov2025,
      value2024: aov2024,
      format: "currency",
      decimals: 0,
    },
    {
      label: "Conv/1k Impr",
      value2025: cpi2025,
      value2024: cpi2024,
      format: "decimal",
      decimals: 2,
      tooltip: "Conversions per 1,000 impressions. Shows how efficiently your ads convert viewers into customers.",
    },
  ];

  return (
    <div className="space-y-3">
      {/* Comparison explainer */}
      <div className="flex items-center justify-end">
        <p className="text-xs text-muted-foreground">
          Comparison: {comparisonLabel}
        </p>
      </div>

      {/* Metric cards grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        {metricCards.map((metric) => (
          <MetricCard key={metric.label} metric={metric} comparisonLabel={comparisonLabel} />
        ))}
      </div>
    </div>
  );
}
